import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCalendarStore } from '../store/calendar';
import { useAuthStore } from '../store/auth';
import { useSettingsStore } from '../store/settings';
import { MonthGrid } from '../components/MonthGrid';
import { MonthGridMobile } from '../components/MonthGridMobile';
import { DayDetailPanel } from '../components/DayDetailPanel';
import { OnboardingTour } from '../components/OnboardingTour';
import { IconButton } from '../components/ui/IconButton';
import { ChevronLeftIcon, ChevronRightIcon, SettingsIcon } from '../components/ui/Icons';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getLunarInfo, today } from '@daynote/utils';
import { authApi } from '../api/auth';
import { isElectron } from '../lib/electron';

// Electron 拖拽区域样式：-webkit-app-region 不在 React 标准 CSSProperties 中，用 unknown 断言
type AppRegionStyle = React.CSSProperties & { WebkitAppRegion?: 'drag' | 'no-drag' };
const dragStyle: AppRegionStyle | undefined = isElectron ? { WebkitAppRegion: 'drag' } : undefined;
const noDragStyle: AppRegionStyle | undefined = isElectron
  ? { WebkitAppRegion: 'no-drag' }
  : undefined;

export function CalendarPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { currentMonth, dayNotesByDate, setMonth, loadMonth, upsertDayNote } =
    useCalendarStore();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const setting = useSettingsStore((s) => s.setting);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  useEffect(() => {
    loadMonth(currentMonth);
  }, [currentMonth, loadMonth]);

  const isEn = i18n.language.startsWith('en');
  const todayStr = today();
  const todayLunar = getLunarInfo(todayStr);
  const [y, m] = currentMonth.split('-').map(Number);
  const weekdaysMon = t('calendar.weekdays_mon', { returnObjects: true }) as string[];
  const weekdaysSun = t('calendar.weekdays_sun', { returnObjects: true }) as string[];
  const weekLabels = setting.weekStart === 0 ? weekdaysSun : weekdaysMon;
  const todayNote = dayNotesByDate[todayStr];
  const todayItemCount = todayNote?.items.length ?? 0;

  // 今日完整信息（顶栏补充行）
  const todayDate = new Date();
  const todayWeekdayNames = t('calendar.weekdayNames', { returnObjects: true }) as string[];
  const todayWeekdayStr = todayWeekdayNames[todayDate.getDay()]; // 0=日/Sun
  const todayMonthNum = todayDate.getMonth() + 1;
  const todayDayNum = todayDate.getDate();
  // zh: "5月26日 星期二 · 四月初十"   en: "May 26, Tue"
  const todayMonthShortEn = todayDate.toLocaleString('en', { month: 'short' });
  const todayInfoStr = isEn
    ? `${todayMonthShortEn} ${todayDayNum}, ${todayWeekdayStr}`
    : `${todayMonthNum}月${todayDayNum}日 星期${todayWeekdayStr} · ${todayLunar.lunarMonth}`;

  const goPrev = useCallback(() => {
    const date = new Date(y!, m! - 1, 1);
    date.setMonth(date.getMonth() - 1);
    setMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }, [y, m, setMonth]);

  const goNext = useCallback(() => {
    const date = new Date(y!, m! - 1, 1);
    date.setMonth(date.getMonth() + 1);
    setMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }, [y, m, setMonth]);

  const goToday = () => {
    const now = new Date();
    setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  };

  const openDay = (date: string) => setSelectedDate(date);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0]?.clientX ?? null);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX;
    if (Math.abs(dx) > 60) {
      if (dx > 0) goPrev();
      else goNext();
    }
    setTouchStartX(null);
  };

  const gridProps = {
    month: currentMonth,
    dayNotesByDate,
    // In English mode, follow Western conventions: hide lunar and Chinese holidays
    showLunar: isEn ? false : setting.showLunar,
    showHoliday: isEn ? false : setting.showHoliday,
    weekStart: setting.weekStart,
    maxRows: setting.maxRows,
  };

  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {/* TOPBAR — 桌面小组件模式下整个顶栏作为拖拽区域 */}
      <header
        className={`flex-shrink-0 relative backdrop-blur-xl bg-gradient-to-b from-black/65 to-black/30 border-b border-[var(--grid-line)] ${
          isMobile ? 'px-5 pt-3.5 pb-2.5 flex flex-col gap-1' : 'flex items-center justify-between px-8 py-4'
        }`}
        style={dragStyle}
      >
        {isMobile ? (
          <>
            <div className="flex items-center justify-between">
              {/* 今日信息替代 logo */}
              <div className="text-xs text-daynote-muted leading-relaxed">
                {todayInfoStr}
              </div>
              <div className="flex gap-1 items-center" style={noDragStyle}>
                <button
                  type="button"
                  onClick={() => navigate('/reports')}
                  className="px-2.5 py-1 text-xs text-daynote-muted hover:text-daynote-accent bg-transparent border-none cursor-pointer"
                >
                  {t('calendar.reports')}
                </button>
                <IconButton onClick={() => navigate('/settings')} label={t('calendar.settings')}>
                  <SettingsIcon />
                </IconButton>
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="font-display text-[28px] font-medium tracking-wide">
                {y} / {String(m).padStart(2, '0')}
              </span>
              <div className="flex gap-1.5" style={noDragStyle}>
                <button
                  type="button"
                  onClick={goPrev}
                  className="w-8 h-8 flex items-center justify-center bg-white/6 rounded-full"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="w-8 h-8 flex items-center justify-center bg-white/6 rounded-full"
                >
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* 左侧：今日完整信息（替代 logo）*/}
            <div className="flex flex-col justify-center select-none min-w-0">
              <div className="text-[13px] text-daynote-text/80 leading-snug tracking-wide">
                {todayInfoStr}
              </div>
            </div>

            {/* 中间：当前浏览月份 */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-baseline gap-3.5 text-sm tracking-wide select-none">
              <span className="font-display text-2xl font-medium">
                {y} / {String(m).padStart(2, '0')}
              </span>
            </div>

            {/* 右侧：导航 + 功能按钮 */}
            <div
              className="flex items-center gap-1.5"
              style={noDragStyle}
            >
              <IconButton onClick={goPrev} label={t('calendar.prevMonth')}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={goNext} label={t('calendar.nextMonth')}>
                <ChevronRightIcon />
              </IconButton>
              <button
                type="button"
                onClick={() => navigate('/reports')}
                className="px-3 py-1 text-sm text-daynote-muted hover:text-daynote-accent bg-transparent border-none cursor-pointer"
              >
                {t('calendar.reports')}
              </button>
              <IconButton onClick={() => navigate('/settings')} label={t('calendar.settings')}>
                <SettingsIcon />
              </IconButton>
              {!isElectron && <LanguageSwitcher className="ml-1" />}
              {/* 云端模式才显示退出按钮；桌面端本地模式不显示 */}
              {!isElectron && (
                <button
                  type="button"
                  onClick={async () => {
                    const refreshToken = useAuthStore.getState().refreshToken;
                    await authApi.logout(refreshToken ?? undefined).catch(() => {});
                    logout();
                    navigate('/login');
                  }}
                  className="ml-2 px-3 py-1 text-sm text-daynote-muted hover:text-daynote-text bg-transparent border-none cursor-pointer"
                >
                  {user?.nickname || t('calendar.logout')}
                </button>
              )}
              {/* 桌面小组件：仅保留透明"隐藏到托盘"按钮，去掉最小化 */}
              {isElectron && (
                <button
                  type="button"
                  title={t('calendar.hideToTray')}
                  onClick={() => window.electronAPI?.hideToTray()}
                  className="ml-1 w-6 h-6 flex items-center justify-center text-daynote-muted/60 hover:text-daynote-text bg-transparent border-none cursor-pointer transition-colors"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </>
        )}
      </header>

      {/* WEEKDAY HEADER */}
      <div
        className="flex-shrink-0 grid border-b border-[var(--grid-line)] bg-black/35 backdrop-blur-sm"
        style={{ gridTemplateColumns: isMobile ? 'repeat(7, 1fr)' : '32px repeat(7, 1fr)' }}
      >
        {!isMobile && (
          <div className="py-3 text-center text-xs text-daynote-muted border-r border-[var(--grid-line)] tracking-widest">
            {t('calendar.weekNum')}
          </div>
        )}
        {weekLabels.map((d, i) => (
          <div
            key={d}
            className={`py-2 md:py-3 text-center text-[11px] md:text-xs tracking-widest ${
              (setting.weekStart === 1 ? i >= 5 : i === 0 || i === 6)
                ? 'text-daynote-warm'
                : 'text-daynote-muted'
            }`}
          >
            {isMobile
              ? `${t('calendar.weekdayPrefixMobile')}${d}`
              : `${t('calendar.weekdayPrefix')}${d}`}
          </div>
        ))}
      </div>

      {/* GRID */}
      {isMobile ? (
        <MonthGridMobile {...gridProps} onDayClick={openDay} />
      ) : (
        <MonthGrid {...gridProps} onDayDoubleClick={openDay} />
      )}

      {/* MOBILE TODAY BAR */}
      {isMobile && currentMonth !== todayStr.slice(0, 7) && (
        <div className="flex-shrink-0 mx-3 mb-3 p-3.5 bg-gradient-to-b from-[rgba(40,32,24,0.75)] to-[rgba(25,20,16,0.85)] backdrop-blur-xl border border-daynote-accent/20 rounded-2xl flex items-center justify-between shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-daynote-accent text-[#2a2520] rounded-lg flex items-center justify-center font-display text-lg font-semibold">
              {new Date().getDate()}
            </div>
            <div>
              <div className="text-[11px] text-daynote-muted tracking-wide">{t('calendar.today')}</div>
              <div className="text-[13px]">
                {todayItemCount > 0 ? t('calendar.itemsCount', { count: todayItemCount }) : t('calendar.noItems')}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={goToday}
            className="bg-daynote-accent text-[#2a2520] px-3.5 py-2 rounded-lg text-[13px] font-medium tracking-wide border-none cursor-pointer"
          >
            {t('calendar.goToday')}
          </button>
        </div>
      )}

      {selectedDate && (
        <DayDetailPanel
          date={selectedDate}
          isMobile={isMobile}
          onClose={() => setSelectedDate(null)}
          onChange={upsertDayNote}
        />
      )}

      <OnboardingTour isMobile={isMobile} />
    </div>
  );
}
